import { BoxConfig, Face, Void, AssemblyConfig, defaultAssemblyConfig, EdgeExtensions, SubAssembly, FaceOffsets, defaultFaceOffsets, PanelPath } from '../types';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import type { AssemblySnapshot, CornerFillet, CornerKey, AllCornerFillet, AllCornerId, Cutout, PanelSnapshot, FacePanelSnapshot, DividerPanelSnapshot, CustomEdgePath, EdgePosition } from '../engine/types';

// Compact serialization format for URL storage
interface SerializedState {
  v: number;  // Version for future compatibility
  c: {        // Config
    w: number;  // width
    h: number;  // height
    d: number;  // depth
    mt: number; // materialThickness
    fw: number; // fingerWidth
    fg: number; // fingerGap
    a?: SerializedAssembly;  // assembly (optional, defaults used if missing)
  };
  f: number;  // Faces bitmap (6 bits, one per face)
  r: SerializedVoid;  // Root void
  e?: Record<string, [number, number, number, number]>;  // Edge extensions by panel ID [top, bottom, left, right]
  po?: Record<string, SerializedPanelOps>;  // Panel operations (fillets, cutouts) by panel ID
}

interface SerializedAssembly {
  ax: 'x' | 'y' | 'z';  // assemblyAxis
  lp?: [string, number];  // lid positive [tabDirection, inset] if not default
  ln?: [string, number];  // lid negative [tabDirection, inset] if not default
}

interface SerializedSubAssembly {
  id: string;
  cl: number;             // clearance
  fo?: [number, number, number, number, number, number];  // faceOffsets [front, back, left, right, top, bottom] if non-zero
  f: number;              // faces bitmap
  rv: SerializedVoid;     // rootVoid
  mt: number;             // materialThickness
  a?: SerializedAssembly; // assembly config
}

interface SerializedGridSubdivision {
  ax: ('x' | 'y' | 'z')[];  // axes
  pos: Partial<Record<'x' | 'y' | 'z', number[]>>;  // positions per axis
}

// =============================================================================
// Panel Operations Serialization Types
// =============================================================================

/**
 * Serialized cutout shape (compact form)
 * Type: 'r' = rect, 'c' = circle, 'p' = path
 */
type SerializedCutout =
  | { t: 'r'; id: string; c: [number, number]; w: number; h: number; cr?: number; m?: 'a' | 's' }  // rect: center, width, height, cornerRadius?, mode?
  | { t: 'c'; id: string; c: [number, number]; r: number; m?: 'a' | 's' }  // circle: center, radius, mode?
  | { t: 'p'; id: string; c: [number, number]; pts: [number, number][]; m?: 'a' | 's' };  // path: center, points, mode?

/**
 * Serialized custom edge path (compact form)
 */
interface SerializedEdgePath {
  e: string;    // edge: 'top'|'bottom'|'left'|'right'
  bo: number;   // baseOffset
  pts: [number, number][];  // points as [t, offset] tuples
  m: boolean;   // mirrored
  f?: number[]; // fillets (optional)
}

/**
 * Serialized panel operations for a single panel
 * - cf: cornerFillets as Record<cornerKey, radius>
 * - acf: allCornerFillets as Record<cornerId, radius>
 * - co: cutouts as SerializedCutout[]
 * - ep: customEdgePaths as SerializedEdgePath[]
 */
interface SerializedPanelOps {
  cf?: Record<string, number>;   // cornerFillets: { "left:top": 5, "bottom:right": 3 }
  acf?: Record<string, number>;  // allCornerFillets: { "outline:5": 2, "hole:cutout-1:0": 3 }
  co?: SerializedCutout[];       // cutouts
  ep?: SerializedEdgePath[];     // customEdgePaths
}

interface SerializedVoid {
  id: string;
  b: [number, number, number, number, number, number];  // bounds [x, y, z, w, h, d]
  ch?: SerializedVoid[];  // children
  sa?: 'x' | 'y' | 'z';   // splitAxis
  sp?: number;            // splitPosition
  sub?: SerializedSubAssembly;  // subAssembly
  gs?: SerializedGridSubdivision;  // gridSubdivision
}

// Face order for bitmap encoding
const FACE_ORDER = ['front', 'back', 'left', 'right', 'top', 'bottom'] as const;

// Round number to 2 decimal places to save space
const r = (n: number): number => Math.round(n * 100) / 100;

// Serialize faces to a bitmap (1 = solid, 0 = open)
const serializeFaces = (faces: Face[]): number => {
  let bitmap = 0;
  for (let i = 0; i < FACE_ORDER.length; i++) {
    const face = faces.find(f => f.id === FACE_ORDER[i]);
    if (face?.solid) {
      bitmap |= (1 << i);
    }
  }
  return bitmap;
};

// Deserialize faces from bitmap
const deserializeFaces = (bitmap: number): Face[] => {
  return FACE_ORDER.map((id, i) => ({
    id,
    solid: (bitmap & (1 << i)) !== 0,
  }));
};

// Serialize face offsets (only if non-zero)
const serializeFaceOffsets = (fo: FaceOffsets): [number, number, number, number, number, number] | undefined => {
  if (fo.front === 0 && fo.back === 0 && fo.left === 0 && fo.right === 0 && fo.top === 0 && fo.bottom === 0) {
    return undefined;
  }
  return [r(fo.front), r(fo.back), r(fo.left), r(fo.right), r(fo.top), r(fo.bottom)];
};

// Deserialize face offsets
const deserializeFaceOffsets = (sfo?: [number, number, number, number, number, number]): FaceOffsets => {
  if (!sfo) return { ...defaultFaceOffsets };
  return {
    front: sfo[0],
    back: sfo[1],
    left: sfo[2],
    right: sfo[3],
    top: sfo[4],
    bottom: sfo[5],
  };
};

// Serialize sub-assembly
const serializeSubAssembly = (sub: SubAssembly): SerializedSubAssembly => {
  return {
    id: sub.id,
    cl: r(sub.clearance),
    fo: serializeFaceOffsets(sub.faceOffsets),
    f: serializeFaces(sub.faces),
    rv: serializeVoid(sub.rootVoid),
    mt: r(sub.materialThickness),
    a: serializeAssembly(sub.assembly),
  };
};

// Deserialize sub-assembly
const deserializeSubAssembly = (ssub: SerializedSubAssembly): SubAssembly => {
  return {
    id: ssub.id,
    clearance: ssub.cl,
    faceOffsets: deserializeFaceOffsets(ssub.fo),
    faces: deserializeFaces(ssub.f),
    rootVoid: deserializeVoid(ssub.rv),
    materialThickness: ssub.mt,
    assembly: deserializeAssembly(ssub.a),
  };
};

// Serialize grid subdivision
const serializeGridSubdivision = (gs: { axes: ('x' | 'y' | 'z')[]; positions: Partial<Record<'x' | 'y' | 'z', number[]>> }): SerializedGridSubdivision => {
  // Round all position values
  const roundedPositions: Partial<Record<'x' | 'y' | 'z', number[]>> = {};
  for (const [axis, positions] of Object.entries(gs.positions)) {
    if (positions) {
      roundedPositions[axis as 'x' | 'y' | 'z'] = positions.map(r);
    }
  }
  return {
    ax: gs.axes,
    pos: roundedPositions,
  };
};

// Deserialize grid subdivision
const deserializeGridSubdivision = (sgs: SerializedGridSubdivision): { axes: ('x' | 'y' | 'z')[]; positions: Partial<Record<'x' | 'y' | 'z', number[]>> } => {
  return {
    axes: sgs.ax,
    positions: sgs.pos,
  };
};

// Serialize void tree
const serializeVoid = (v: Void): SerializedVoid => {
  const result: SerializedVoid = {
    id: v.id,
    b: [r(v.bounds.x), r(v.bounds.y), r(v.bounds.z), r(v.bounds.w), r(v.bounds.h), r(v.bounds.d)],
  };

  if (v.children && v.children.length > 0) {
    result.ch = v.children.map(serializeVoid);
  }
  if (v.splitAxis) {
    result.sa = v.splitAxis;
  }
  if (v.splitPosition !== undefined) {
    result.sp = r(v.splitPosition);
  }
  if (v.subAssembly) {
    result.sub = serializeSubAssembly(v.subAssembly);
  }
  if (v.gridSubdivision) {
    result.gs = serializeGridSubdivision(v.gridSubdivision);
  }

  return result;
};

// Deserialize void tree
const deserializeVoid = (sv: SerializedVoid): Void => {
  const result: Void = {
    id: sv.id,
    bounds: {
      x: sv.b[0],
      y: sv.b[1],
      z: sv.b[2],
      w: sv.b[3],
      h: sv.b[4],
      d: sv.b[5],
    },
    children: sv.ch ? sv.ch.map(deserializeVoid) : [],
    splitAxis: sv.sa,
    splitPosition: sv.sp,
  };

  if (sv.sub) {
    result.subAssembly = deserializeSubAssembly(sv.sub);
  }

  if (sv.gs) {
    result.gridSubdivision = deserializeGridSubdivision(sv.gs);
  }

  return result;
};

// Serialize assembly config (only non-default values)
const serializeAssembly = (a: AssemblyConfig): SerializedAssembly | undefined => {
  const isDefault =
    a.assemblyAxis === 'y' &&
    a.lids.positive.tabDirection === 'tabs-out' &&
    a.lids.positive.inset === 0 &&
    a.lids.negative.tabDirection === 'tabs-out' &&
    a.lids.negative.inset === 0;

  if (isDefault) return undefined;

  const result: SerializedAssembly = { ax: a.assemblyAxis };

  if (a.lids.positive.tabDirection !== 'tabs-out' || a.lids.positive.inset !== 0) {
    result.lp = [a.lids.positive.tabDirection, a.lids.positive.inset];
  }
  if (a.lids.negative.tabDirection !== 'tabs-out' || a.lids.negative.inset !== 0) {
    result.ln = [a.lids.negative.tabDirection, a.lids.negative.inset];
  }

  return result;
};

// Deserialize assembly config
const deserializeAssembly = (sa?: SerializedAssembly): AssemblyConfig => {
  if (!sa) return { ...defaultAssemblyConfig };

  return {
    assemblyAxis: sa.ax,
    lids: {
      positive: {
        enabled: true,
        tabDirection: (sa.lp?.[0] as 'tabs-out' | 'tabs-in') ?? 'tabs-out',
        inset: sa.lp?.[1] ?? 0,
      },
      negative: {
        enabled: true,
        tabDirection: (sa.ln?.[0] as 'tabs-out' | 'tabs-in') ?? 'tabs-out',
        inset: sa.ln?.[1] ?? 0,
      },
    },
  };
};

// Serialize edge extensions (only non-zero)
const serializeExtensions = (
  extensions: Record<string, EdgeExtensions>
): Record<string, [number, number, number, number]> | undefined => {
  const result: Record<string, [number, number, number, number]> = {};
  let hasAny = false;

  for (const [panelId, ext] of Object.entries(extensions)) {
    if (ext.top !== 0 || ext.bottom !== 0 || ext.left !== 0 || ext.right !== 0) {
      result[panelId] = [ext.top, ext.bottom, ext.left, ext.right];
      hasAny = true;
    }
  }

  return hasAny ? result : undefined;
};

// Deserialize edge extensions
const deserializeExtensions = (
  se?: Record<string, [number, number, number, number]>
): Record<string, EdgeExtensions> => {
  if (!se) return {};

  const result: Record<string, EdgeExtensions> = {};
  for (const [panelId, [top, bottom, left, right]] of Object.entries(se)) {
    result[panelId] = { top, bottom, left, right };
  }
  return result;
};

// =============================================================================
// Panel Operations Serialization
// =============================================================================

/**
 * Serialize a single cutout to compact format
 */
const serializeCutout = (cutout: Cutout): SerializedCutout => {
  const mode = cutout.mode === 'additive' ? 'a' : cutout.mode === 'subtractive' ? 's' : undefined;

  switch (cutout.type) {
    case 'rect': {
      const result: SerializedCutout = {
        t: 'r',
        id: cutout.id,
        c: [r(cutout.center.x), r(cutout.center.y)],
        w: r(cutout.width),
        h: r(cutout.height),
      };
      if (cutout.cornerRadius !== undefined && cutout.cornerRadius !== 0) {
        result.cr = r(cutout.cornerRadius);
      }
      if (mode) {
        result.m = mode;
      }
      return result;
    }
    case 'circle': {
      const result: SerializedCutout = {
        t: 'c',
        id: cutout.id,
        c: [r(cutout.center.x), r(cutout.center.y)],
        r: r(cutout.radius),
      };
      if (mode) {
        result.m = mode;
      }
      return result;
    }
    case 'path': {
      const result: SerializedCutout = {
        t: 'p',
        id: cutout.id,
        c: [r(cutout.center.x), r(cutout.center.y)],
        pts: cutout.points.map(p => [r(p.x), r(p.y)] as [number, number]),
      };
      if (mode) {
        result.m = mode;
      }
      return result;
    }
  }
};

/**
 * Get a stable key for a panel snapshot that survives engine restarts.
 * Face panels are keyed by faceId; divider panels by voidId:axis:position.
 */
export const getPanelCanonicalKey = (panel: PanelSnapshot): string => {
  if (panel.kind === 'face-panel') {
    const facePanel = panel as FacePanelSnapshot;
    const prefix = facePanel.props.assemblyId
      ? `sub:${facePanel.props.assemblyId}:face:${facePanel.props.faceId}`
      : `face:${facePanel.props.faceId}`;
    return prefix;
  } else {
    const dividerPanel = panel as DividerPanelSnapshot;
    return `divider:${dividerPanel.props.voidId}:${dividerPanel.props.axis}:${r(dividerPanel.props.position)}`;
  }
};

/** @deprecated Use getPanelCanonicalKey instead */
export const getPanelStableKey = getPanelCanonicalKey;

/**
 * Get a stable canonical key from a PanelPath (which has PanelSource metadata).
 * Same key format as getPanelCanonicalKey but works with PanelPath objects
 * from generatePanelsFromNodes().
 */
export const getPanelCanonicalKeyFromPath = (panel: PanelPath): string => {
  const source = panel.source;
  if (source.type === 'face') {
    return source.subAssemblyId
      ? `sub:${source.subAssemblyId}:face:${source.faceId}`
      : `face:${source.faceId}`;
  } else {
    return `divider:${source.subdivisionId}:${source.axis}:${r(source.position ?? 0)}`;
  }
};

/**
 * Serialize panel operations from assembly snapshot.
 * Extracts cornerFillets, allCornerFillets, and cutouts from each panel.
 * Uses stable source-based keys (not UUIDs) so operations survive engine restarts.
 *
 * @param assemblySnapshot - The assembly snapshot containing panels
 * @returns Record<stableKey, SerializedPanelOps> or undefined if no operations
 */
export const serializePanelOperations = (
  assemblySnapshot: AssemblySnapshot
): Record<string, SerializedPanelOps> | undefined => {
  const result: Record<string, SerializedPanelOps> = {};
  let hasAny = false;

  for (const panel of assemblySnapshot.derived.panels) {
    const ops: SerializedPanelOps = {};
    let hasOps = false;

    // Serialize corner fillets: CornerFillet[] -> Record<cornerKey, radius>
    if (panel.props.cornerFillets && panel.props.cornerFillets.length > 0) {
      const cf: Record<string, number> = {};
      for (const fillet of panel.props.cornerFillets) {
        cf[fillet.corner] = r(fillet.radius);
      }
      ops.cf = cf;
      hasOps = true;
    }

    // Serialize all-corner fillets: AllCornerFillet[] -> Record<cornerId, radius>
    if (panel.props.allCornerFillets && panel.props.allCornerFillets.length > 0) {
      const acf: Record<string, number> = {};
      for (const fillet of panel.props.allCornerFillets) {
        acf[fillet.cornerId] = r(fillet.radius);
      }
      ops.acf = acf;
      hasOps = true;
    }

    // Serialize cutouts: Cutout[] -> SerializedCutout[]
    if (panel.props.cutouts && panel.props.cutouts.length > 0) {
      ops.co = panel.props.cutouts.map(serializeCutout);
      hasOps = true;
    }

    // Serialize custom edge paths: CustomEdgePath[] -> SerializedEdgePath[]
    if (panel.props.customEdgePaths && panel.props.customEdgePaths.length > 0) {
      ops.ep = panel.props.customEdgePaths.map(ep => ({
        e: ep.edge,
        bo: r(ep.baseOffset),
        pts: ep.points.map(p => [r(p.t), r(p.offset)] as [number, number]),
        m: ep.mirrored,
        ...(ep.fillets && ep.fillets.length > 0 ? { f: ep.fillets.map(r) } : {}),
      }));
      hasOps = true;
    }

    if (hasOps) {
      const stableKey = getPanelCanonicalKey(panel);
      result[stableKey] = ops;
      hasAny = true;
    }
  }

  return hasAny ? result : undefined;
};

/**
 * Deserialize a single cutout from compact format back to full Cutout type
 */
const deserializeCutout = (sc: SerializedCutout): Cutout => {
  const mode = sc.m === 'a' ? 'additive' : sc.m === 's' ? 'subtractive' : undefined;

  switch (sc.t) {
    case 'r': {
      const result: Cutout = {
        type: 'rect',
        id: sc.id,
        center: { x: sc.c[0], y: sc.c[1] },
        width: sc.w,
        height: sc.h,
      };
      if (sc.cr !== undefined) {
        result.cornerRadius = sc.cr;
      }
      if (mode) {
        result.mode = mode;
      }
      return result;
    }
    case 'c': {
      const result: Cutout = {
        type: 'circle',
        id: sc.id,
        center: { x: sc.c[0], y: sc.c[1] },
        radius: sc.r,
      };
      if (mode) {
        result.mode = mode;
      }
      return result;
    }
    case 'p': {
      const result: Cutout = {
        type: 'path',
        id: sc.id,
        center: { x: sc.c[0], y: sc.c[1] },
        points: sc.pts.map(([x, y]) => ({ x, y })),
      };
      if (mode) {
        result.mode = mode;
      }
      return result;
    }
  }
};

/**
 * Output format for deserialized panel operations.
 * Maps panel IDs to their operations for use in dispatching engine actions.
 */
export interface DeserializedPanelOps {
  cornerFillets: CornerFillet[];
  allCornerFillets: AllCornerFillet[];
  cutouts: Cutout[];
  customEdgePaths: CustomEdgePath[];
}

/**
 * Deserialize panel operations from compact format back to full types.
 * Returns a map of panel IDs to their operations.
 *
 * @param spo - Serialized panel operations (from SerializedState.po)
 * @returns Record<panelId, DeserializedPanelOps> - empty object if input is undefined
 */
export const deserializePanelOperations = (
  spo?: Record<string, SerializedPanelOps>
): Record<string, DeserializedPanelOps> => {
  if (!spo) return {};

  const result: Record<string, DeserializedPanelOps> = {};

  for (const [panelId, ops] of Object.entries(spo)) {
    const cornerFillets: CornerFillet[] = [];
    const allCornerFillets: AllCornerFillet[] = [];
    const cutouts: Cutout[] = [];

    // Reconstruct CornerFillet[] from Record<cornerKey, radius>
    if (ops.cf) {
      for (const [corner, radius] of Object.entries(ops.cf)) {
        cornerFillets.push({ corner: corner as CornerKey, radius });
      }
    }

    // Reconstruct AllCornerFillet[] from Record<cornerId, radius>
    if (ops.acf) {
      for (const [cornerId, radius] of Object.entries(ops.acf)) {
        allCornerFillets.push({ cornerId: cornerId as AllCornerId, radius });
      }
    }

    // Reconstruct Cutout[] from SerializedCutout[]
    if (ops.co) {
      for (const sc of ops.co) {
        cutouts.push(deserializeCutout(sc));
      }
    }

    // Reconstruct CustomEdgePath[] from SerializedEdgePath[]
    const customEdgePaths: CustomEdgePath[] = [];
    if (ops.ep) {
      for (const sep of ops.ep) {
        customEdgePaths.push({
          edge: sep.e as EdgePosition,
          baseOffset: sep.bo,
          points: sep.pts.map(([t, offset]) => ({ t, offset })),
          mirrored: sep.m,
          ...(sep.f ? { fillets: sep.f } : {}),
        });
      }
    }

    result[panelId] = { cornerFillets, allCornerFillets, cutouts, customEdgePaths };
  }

  return result;
};

// Main serialization function
export interface ProjectState {
  config: BoxConfig;
  faces: Face[];
  rootVoid: Void;
  edgeExtensions: Record<string, EdgeExtensions>;
  panelOperations?: Record<string, DeserializedPanelOps>;
}

/**
 * Re-serialize deserialized panel operations back to compact format.
 * Used by serializeProject when ProjectState includes panelOperations.
 */
const reserializePanelOps = (
  ops: Record<string, DeserializedPanelOps>
): Record<string, SerializedPanelOps> | undefined => {
  const result: Record<string, SerializedPanelOps> = {};
  let hasAny = false;

  for (const [panelId, panelOps] of Object.entries(ops)) {
    const serialized: SerializedPanelOps = {};
    let hasOps = false;

    if (panelOps.cornerFillets && panelOps.cornerFillets.length > 0) {
      const cf: Record<string, number> = {};
      for (const fillet of panelOps.cornerFillets) {
        cf[fillet.corner] = r(fillet.radius);
      }
      serialized.cf = cf;
      hasOps = true;
    }

    if (panelOps.allCornerFillets && panelOps.allCornerFillets.length > 0) {
      const acf: Record<string, number> = {};
      for (const fillet of panelOps.allCornerFillets) {
        acf[fillet.cornerId] = r(fillet.radius);
      }
      serialized.acf = acf;
      hasOps = true;
    }

    if (panelOps.cutouts && panelOps.cutouts.length > 0) {
      serialized.co = panelOps.cutouts.map(serializeCutout);
      hasOps = true;
    }

    if (panelOps.customEdgePaths && panelOps.customEdgePaths.length > 0) {
      serialized.ep = panelOps.customEdgePaths.map(ep => ({
        e: ep.edge,
        bo: r(ep.baseOffset),
        pts: ep.points.map(p => [r(p.t), r(p.offset)] as [number, number]),
        m: ep.mirrored,
        ...(ep.fillets && ep.fillets.length > 0 ? { f: ep.fillets.map(r) } : {}),
      }));
      hasOps = true;
    }

    if (hasOps) {
      result[panelId] = serialized;
      hasAny = true;
    }
  }

  return hasAny ? result : undefined;
};

export const serializeProject = (state: ProjectState): string => {
  const serialized: SerializedState = {
    v: 1,
    c: {
      w: r(state.config.width),
      h: r(state.config.height),
      d: r(state.config.depth),
      mt: r(state.config.materialThickness),
      fw: r(state.config.fingerWidth),
      fg: r(state.config.fingerGap),
      a: serializeAssembly(state.config.assembly),
    },
    f: serializeFaces(state.faces),
    r: serializeVoid(state.rootVoid),
    e: serializeExtensions(state.edgeExtensions),
    po: state.panelOperations ? reserializePanelOps(state.panelOperations) : undefined,
  };

  // Convert to JSON and compress with lz-string
  const json = JSON.stringify(serialized);
  const compressed = compressToEncodedURIComponent(json);
  return compressed;
};

// Try to deserialize with lz-string first, fall back to old base64 format
export const deserializeProject = (encoded: string): ProjectState | null => {
  try {
    // Try lz-string decompression first
    let json = decompressFromEncodedURIComponent(encoded);

    // Fall back to old base64 format if lz-string fails
    if (!json) {
      json = decodeURIComponent(atob(encoded));
    }

    const serialized: SerializedState = JSON.parse(json);

    // Version check for future compatibility
    if (serialized.v !== 1) {
      console.warn('Unknown project version:', serialized.v);
    }

    const config: BoxConfig = {
      width: serialized.c.w,
      height: serialized.c.h,
      depth: serialized.c.d,
      materialThickness: serialized.c.mt,
      fingerWidth: serialized.c.fw,
      fingerGap: serialized.c.fg,
      assembly: deserializeAssembly(serialized.c.a),
    };

    const panelOperations = deserializePanelOperations(serialized.po);

    return {
      config,
      faces: deserializeFaces(serialized.f),
      rootVoid: deserializeVoid(serialized.r),
      edgeExtensions: deserializeExtensions(serialized.e),
      panelOperations: Object.keys(panelOperations).length > 0 ? panelOperations : undefined,
    };
  } catch (e) {
    console.error('Failed to deserialize project:', e);
    return null;
  }
};

// URL helpers - using query parameter for better sharing compatibility
const URL_PARAM = 'p';

export const saveToUrl = (state: ProjectState): void => {
  const encoded = serializeProject(state);
  const url = new URL(window.location.href);
  url.searchParams.set(URL_PARAM, encoded);
  url.hash = ''; // Clear any old hash
  window.history.replaceState(null, '', url.toString());
};

export const loadFromUrl = (): ProjectState | null => {
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get(URL_PARAM);

  // Also check hash for backwards compatibility with old URLs
  if (!encoded) {
    const hash = window.location.hash.slice(1);
    if (hash) return deserializeProject(hash);
    return null;
  }

  return deserializeProject(encoded);
};

export const clearUrlState = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete(URL_PARAM);
  url.hash = '';
  window.history.replaceState(null, '', url.toString());
};

export const getShareableUrl = (state: ProjectState): string => {
  const encoded = serializeProject(state);
  const url = new URL(window.location.href);
  url.searchParams.set(URL_PARAM, encoded);
  url.hash = '';
  return url.toString();
};
